import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useConstant, useScreen } from '../../../../hooks'
import { useCloseConfirm } from '../../../Form'
import {
  MINIMIZED_MODAL_KEY,
  MODAL_HEADER_VARIANT,
  useMinimizableModal,
  useResizeModal,
} from '../../../Modal'
import { RentalPortalApplicantForm } from '../RentalPortalApplicantForm'
import { RentalPortalApplicantFormPopupContainer } from '../RentalPortalApplicantFormPopupContainer'
import { APPLICANT_MINIMIZED_REPLACE_CONFIRMATION } from '../RentalPortalApplicants.constants'
import {
  useRentalPortalApplicantDraft,
  useRentalPortalApplicantDraftCloseConfirmation,
} from '../RentalPortalApplicants.hooks'

import { QA_NAME_APPLICANT_CREATION_MODAL } from './RentalPortalApplicantFormModal.constants'
import { getIsApplicantFormDirty } from './RentalPortalApplicantFormModal.utils'

function RentalPortalApplicantFormModal({
  initialValues,
  isDirtyFromOpen,
  onClose,
  onSuccess,
  ...restProps
}) {
  const { t } = useTranslation()

  const { isMobileScreenSize } = useScreen()

  const opener = useConstant(() => document.activeElement)

  const getIsDirty = getIsApplicantFormDirty(isDirtyFromOpen)

  const { handleClose, isCloseConfirm, setCloseConfirm } = useCloseConfirm({
    getIsDirty,
    onClose,
  })

  const { fullScreen, handleResizeModal } = useResizeModal()

  const { draftId, setDraft } = useRentalPortalApplicantDraft()

  const draftCloseConfirmation =
    useRentalPortalApplicantDraftCloseConfirmation(setDraft)

  const { handleMinimize, isMinimized, setIsDirty } = useMinimizableModal({
    closeConfirmation: draftCloseConfirmation,
    minimizeKey: MINIMIZED_MODAL_KEY.RENTAL_PORTAL_APPLICANT_FORM,
    qaName: QA_NAME_APPLICANT_CREATION_MODAL,
    replaceConfirmation: APPLICANT_MINIMIZED_REPLACE_CONFIRMATION,
    sourceId: draftId,
    title: t('RentalPortal.ApplicantAddNewBtnKey'),
    onClose,
  })

  useEffect(() => () => opener?.focus(), [opener])

  const isFullScreen = isMobileScreenSize || fullScreen

  return (
    <RentalPortalApplicantForm
      {...restProps}
      FormContainer={RentalPortalApplicantFormPopupContainer}
      containerProps={{
        getIsDirty,
        headerVariant: MODAL_HEADER_VARIANT.CREATE,
        hidden: isMinimized,
        isCloseConfirm,
        isFullScreen,
        qaName: QA_NAME_APPLICANT_CREATION_MODAL,
        setCloseConfirm,
        title: 'RentalPortal.ApplicantAddNewBtnKey',
        onClose,
        onIsDirtyChange: setIsDirty,
        onMinimize: handleMinimize,
        onResize: isMobileScreenSize ? undefined : handleResizeModal,
      }}
      contentComponentProps={{ isFullScreen }}
      initialValues={initialValues}
      isMinimized={isMinimized}
      onCancel={handleClose}
      onSuccess={onSuccess}
    />
  )
}

export default RentalPortalApplicantFormModal
